import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import betslip from '@gin-betting/betslip';
import type { App, GQL } from '@gin-betting/core';
import { createStructuredSelector } from 'reselect';
import type { Arg1 } from 'tsargs';

import globalMsg from 'messages';
import ui from 'modules/ui';
import UserDevice from 'services/user-device';
import type { State } from 'types';
import messages from '../../messages';

const {
    actions: { closeSidebar },
} = ui;

const {
    selectors: { selectBetslipBetWithStatus, selectOddUpdateStatus },
    actions: { changeOddUpdateStatus, deleteBetslipOdds, choseBetslipBetId },
} = betslip;

const selectProps = createStructuredSelector({
    betslipBet: selectBetslipBetWithStatus,
    oddUpdateStatus: selectOddUpdateStatus,
});

const MIN_BETS_COUNT_TO_FIT_SUCCESS_TEXT = 2 as const;
const MIN_BETS_COUNT_TO_FIT_BETS_COUNT = 3 as const;

interface Input {
    betslipBetId: string;
    isOpened: boolean;
    betslipBetsSize: number;
}

interface Output {
    status: App.BetslipStatus;
    choseBetslipBetId: VoidFn;
    deleteBetslipOdds: (event: React.MouseEvent<SVGSVGElement>) => void;
    betsSize: number;
    mode: GQL.BetType;
    deleteTitle: string;
    successTitle: string;
    betCountTitle: string;
    changeOddUpdateStatus: typeof changeOddUpdateStatus;
    oddUpdateStatus: App.OddUpdateStatus;
}

const useBetslipHeader = ({
    betslipBetId,
    isOpened,
    betslipBetsSize,
}: Input): Output | null => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();

    const handlerChoseBetslipBetId = useCallback(() => {
        if (isOpened) return;

        dispatch(choseBetslipBetId({ betslipBetId }));
    }, [dispatch, betslipBetId, isOpened]);

    const handleDeleteBetslipOdds = useCallback(
        (event: React.MouseEvent<SVGSVGElement>) => {
            event.stopPropagation();

            dispatch(
                deleteBetslipOdds({
                    betslipBetId,
                })
            );
            if (!UserDevice.getInstance()?.isDeviceMobile()) {
                dispatch(closeSidebar());
            }
        },
        [dispatch, betslipBetId]
    );

    const changeOddUpdateStatusHandle: typeof changeOddUpdateStatus = useCallback(
        (payload: Arg1<typeof changeOddUpdateStatus>) =>
            dispatch(changeOddUpdateStatus(payload)),
        [dispatch]
    );

    const { betslipBet, oddUpdateStatus } = useSelector((state: State) =>
        selectProps(state, { betslipBetId })
    );

    if (!betslipBet) return null;

    const { betslipOddIds, mode, status } = betslipBet;

    const betsSize = betslipOddIds.count();

    const successTitle =
        betslipBetsSize <= MIN_BETS_COUNT_TO_FIT_SUCCESS_TEXT || isOpened
            ? formatMessage(messages.completed)
            : '';
    const deleteTitle = formatMessage(messages.deleteAllBetslipOdds);
    const betCountTitle =
        betslipBetsSize <= MIN_BETS_COUNT_TO_FIT_BETS_COUNT || isOpened
            ? formatMessage(globalMsg.bettingSlipBetTitle, {
                  count: betsSize,
              })
            : '';

    return {
        status,
        choseBetslipBetId: handlerChoseBetslipBetId,
        deleteBetslipOdds: handleDeleteBetslipOdds,
        changeOddUpdateStatus: changeOddUpdateStatusHandle,
        betsSize,
        mode,
        deleteTitle,
        successTitle,
        betCountTitle,
        oddUpdateStatus,
    };
};

export default useBetslipHeader;
